import { useIsFocused } from '@react-navigation/native';
import { router, useLocalSearchParams, usePathname } from 'expo-router';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Platform, View, useWindowDimensions } from 'react-native';

import { ActionSheet, NotFound } from '@fhs/ui';
import { getFirstSearchParam } from '@fhs/utils';
import Footer from '@fhs-legacy/frontend/src/components/footer';
import { useIsDesktopBp } from '@fhs-legacy/frontend/src/hooks/breakpoints';
import { useAutoPromptLocationPermission } from '@fhs-legacy/frontend/src/pages/store-locator/new-ui/hooks/use-auto-prompt-location-permission';
import { useStoreLocatorContext } from '@fhs-legacy/frontend/src/pages/store-locator/new-ui/store-locator-provider';
import { useAuthContext } from '@fhs-legacy/frontend/src/state/auth';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import { useGeolocation } from '@fhs-legacy/frontend/src/state/geolocation';
import { OrderLocatorTab } from '@fhs-legacy/frontend/src/utils/launchdarkly/variations';
import { STORE_LOCATOR_CONTAINER } from '@fhs-legacy/frontend/src/utils/test-ids';

import { DeliveryPlaceholder } from '../components/delivery-placeholder';
import { TAB_LABEL_IDS } from '../components/locator-tabs';
import { MapPane } from '../components/map-pane';
import { PanelHeader } from '../components/panel-header';
import { LocationSearchBar, LocationSearchBarHandle } from '../components/search-field';
import { ServiceModeToggle } from '../components/service-mode-toggle';
import { StoreDetails } from '../components/store-details';
import { StoreListPager } from '../components/store-list-pager';
import { useCloseStoreDetails } from '../hooks/use-close-store-details';
import { useCloseStoreLocator } from '../hooks/use-close-store-locator';
import { useLocatorRestaurants } from '../hooks/use-locator-restaurants';
import { useStoreDetailsRestaurant } from '../hooks/use-store-details-restaurant';
import { StoreLocatorServiceModeTab } from '../types';

import { useStoreLocatorV2MqStyles } from './store-locator-v2.styles';

const isWeb = Platform.OS === 'web';
const PANEL_TITLE_ID = 'store-locator-panel-title';
const LIST_PANEL_ID = 'store-locator-list-panel';

const getServiceModeTabFromParam = (
  tab: string | string[] | undefined
): StoreLocatorServiceModeTab | undefined => {
  const value = getFirstSearchParam(tab)?.toLowerCase();

  return Object.values(StoreLocatorServiceModeTab).find(mode => mode === value);
};

export const StoreLocatorV2View: React.FC = () => {
  const { formatMessage } = useIntl();
  const styles = useStoreLocatorV2MqStyles();
  const isDesktop = useIsDesktopBp();
  const pathname = usePathname();
  const { height: windowHeight } = useWindowDimensions();

  // Desktop keeps the map flush to the viewport edge, so the site footer is mobile-web only.
  const showFooter = isWeb && !isDesktop;

  const { isAuthenticated } = useAuthContext();
  const { logStoreLocatorTabView } = useCRMEventsContext();
  const { manualAddress } = useGeolocation();
  const {
    ErrorDialog,
    Dialog,
    activeTab,
    setActiveTab,
    selectedRestaurantId,
    setSelectedRestaurantId,
    serviceModeTab,
    setServiceModeTab,
  } = useStoreLocatorContext();
  const { tab, storeId } = useLocalSearchParams<{
    tab?: string | string[];
    storeId?: string | string[];
  }>();
  const tabFromRoute = useMemo(() => getServiceModeTabFromParam(tab), [tab]);
  const storeDetailsId = useMemo(() => getFirstSearchParam(storeId), [storeId]);
  const isFocused = useIsFocused();

  // Every locator screen in the stack shares one provider, including copies preloaded out of
  // sight, so only the one on screen may put its tab into it.
  useLayoutEffect(() => {
    if (!isFocused || !tabFromRoute) {
      return;
    }

    setServiceModeTab(tabFromRoute);
  }, [isFocused, tabFromRoute, setServiceModeTab]);

  // The toggle writes the tab into the URL rather than the provider, leaving the effect above
  // its only writer. Otherwise a return to this screen would restore the tab it opened on.
  const onServiceModeTabChange = useCallback(
    (nextTab: StoreLocatorServiceModeTab) => router.setParams({ tab: nextTab }),
    []
  );

  useAutoPromptLocationPermission();

  const closeStoreLocator = useCloseStoreLocator();
  const { byTab, allRestaurants, favoriteRestaurants } = useLocatorRestaurants();

  // Guests only ever see Nearby — Previous and Favorites both require an account.
  const availableTabs = useMemo(
    () =>
      isAuthenticated
        ? [OrderLocatorTab.NEARBY, OrderLocatorTab.RECENT, OrderLocatorTab.FAVORITE]
        : [OrderLocatorTab.NEARBY],
    [isAuthenticated]
  );

  const onTabChange = useCallback(
    (nextTab: OrderLocatorTab) => {
      setActiveTab(nextTab);
      logStoreLocatorTabView(pathname, formatMessage({ id: TAB_LABEL_IDS[nextTab] ?? 'nearby' }));
    },
    [setActiveTab, logStoreLocatorTabView, pathname, formatMessage]
  );

  // Bumped on every select, including a repeat tap of the same store, so the map
  // still pans back after the guest has moved away (recenter, drag, etc.).
  const [selectionFocusVersion, setSelectionFocusVersion] = useState(0);

  const onSelectRestaurant = useCallback(
    (restaurantId: string) => {
      setSelectedRestaurantId(restaurantId);
      setSelectionFocusVersion(version => version + 1);
    },
    [setSelectedRestaurantId]
  );
  const onClearSelection = useCallback(
    () => setSelectedRestaurantId(null),
    [setSelectedRestaurantId]
  );

  const closeStoreDetails = useCloseStoreDetails(storeDetailsId);

  // Opening details focuses that store's pin, wherever the open came from — a card, or a
  // redirect from the old details URL.
  useEffect(() => {
    if (storeDetailsId) {
      onSelectRestaurant(storeDetailsId);
    }
  }, [storeDetailsId, onSelectRestaurant]);

  const { restaurant: storeDetailsRestaurant, loading: isStoreDetailsLoading } =
    useStoreDetailsRestaurant(storeDetailsId, allRestaurants);

  // Details is about one store, so the map shows that store alone — the rest belong to the
  // list the guest came from and would only compete with it. A store opened by link is often
  // outside the results the locator loaded, so this is also what lets the map pin it at all.
  const mapRestaurants = useMemo(() => {
    if (!storeDetailsId) {
      return allRestaurants;
    }

    return storeDetailsRestaurant ? [storeDetailsRestaurant] : [];
  }, [allRestaurants, storeDetailsId, storeDetailsRestaurant]);

  const showStoreDetails = Boolean(storeDetailsId);

  // The sheet dismisses itself on Escape. Desktop has no sheet, so the panel handles it.
  useEffect(() => {
    if (!isWeb || !isDesktop || !showStoreDetails) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeStoreDetails();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isDesktop, showStoreDetails, closeStoreDetails]);

  const searchBarRef = useRef<LocationSearchBarHandle>(null);
  const onSearchReset = useCallback(() => {
    searchBarRef.current?.clear();
  }, []);

  // Searching an area or an address refetches the nearby stores, so the guest has to be on the
  // tab that shows them. Set directly rather than through `onTabChange`, which logs a tab view
  // the guest did not ask for.
  const onSearchThisArea = useCallback(() => setActiveTab(OrderLocatorTab.NEARBY), [setActiveTab]);
  const onSelectLocation = useCallback(() => {
    onClearSelection();
    setTimeout(() => setActiveTab(OrderLocatorTab.NEARBY), 300);
  }, [onClearSelection, setActiveTab]);

  const isDelivery = serviceModeTab === StoreLocatorServiceModeTab.DELIVERY;

  // A store id that resolves to nothing is a missing page — the locator has no store to
  // show, so it gives way to the same not-found screen the rest of the site uses.
  if (storeDetailsId && !isStoreDetailsLoading && !storeDetailsRestaurant) {
    return <NotFound />;
  }

  const mapPane = (
    <View style={styles.mapPane}>
      <MapPane
        restaurants={mapRestaurants}
        favoriteRestaurants={favoriteRestaurants}
        selectedRestaurantId={selectedRestaurantId}
        selectionFocusVersion={selectionFocusVersion}
        isSelectionLoading={isStoreDetailsLoading}
        onSelectRestaurant={onSelectRestaurant}
        onClearSelection={onClearSelection}
        showSearchOverlay={!isDesktop}
        onSearchReset={onSearchReset}
        onSelectLocation={onSelectLocation}
        showSearchThisArea={!isDelivery}
        onSearchThisArea={onSearchThisArea}
        // Details owns the screen while it is open, and the map behind it is only there to
        // place the store it is about, so it holds the frame details opened it on.
        isStatic={showStoreDetails}
      />
    </View>
  );

  const listArea = isDelivery ? (
    <DeliveryPlaceholder />
  ) : (
    <StoreListPager
      tabs={availableTabs}
      activeTab={activeTab}
      onTabChange={onTabChange}
      restaurantsByTab={byTab}
      selectedRestaurantId={selectedRestaurantId}
      onSelectRestaurant={onSelectRestaurant}
      hasSearchedLocation={Boolean(manualAddress)}
      panelId={LIST_PANEL_ID}
    />
  );

  const storeDetails = storeDetailsId ? (
    <StoreDetails
      restaurant={storeDetailsRestaurant}
      isLoading={isStoreDetailsLoading}
      onClose={closeStoreDetails}
    />
  ) : null;

  // Desktop hands the whole panel over to the details pane. Below desktop the shared bottom
  // sheet rises over the locator, so the map stays mounted behind it.
  const showDetailsInPanel = isDesktop && showStoreDetails;
  const showDetailsSheet = !isDesktop && showStoreDetails;

  return (
    <>
      <View
        testID={STORE_LOCATOR_CONTAINER}
        style={[styles.root, showFooter && [styles.rootWithFooter, { minHeight: windowHeight }]]}
        {...(isWeb
          ? ({ role: 'region', 'aria-labelledby': PANEL_TITLE_ID } as Record<string, unknown>)
          : {})}
      >
        <View style={styles.panel}>
          {showDetailsInPanel ? (
            storeDetails
          ) : (
            <>
              <PanelHeader onClose={closeStoreLocator} titleNativeId={PANEL_TITLE_ID} />

              <View style={styles.panelControls}>
                <ServiceModeToggle value={serviceModeTab} onChange={onServiceModeTabChange} />
                {isDesktop && !isDelivery && (
                  <LocationSearchBar
                    ref={searchBarRef}
                    placeholder="Enter a location"
                    onSelectLocation={onSelectLocation}
                  />
                )}
              </View>

              {/* Stacked layouts sit the map between the toggle and the tabs. */}
              {!isDesktop && mapPane}

              <View style={styles.listArea}>{listArea}</View>
            </>
          )}
        </View>

        {isDesktop && mapPane}

        {showDetailsSheet && (
          <ActionSheet
            isVisible
            onClose={closeStoreDetails}
            sheetAccessibilityLabel="Store details"
            hideHeader
            grabber
            ignoreBottomSafeArea
            containerStyle={styles.detailsSheet}
            drawerProps={{ style: styles.detailsSheetBody }}
          >
            {storeDetails}
          </ActionSheet>
        )}
      </View>

      {showFooter && <Footer />}

      <ErrorDialog />
      <Dialog />
    </>
  );
};
